// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Types for Policy Metadata feature.
 * Corresponds to backend DTOs in com.loa.dto.policymetadata package.
 */

export interface PolicyStructuredDetails {
  id?: string;
  policyId?: string;
  brokerageId?: string;
  fields: Record<string, unknown>;
  associatedDocumentVersion?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface PolicyCustomMetadata {
  id: string;
  policyId: string;
  key: string;
  value: string;
  associatedDocumentVersion?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface PolicyMetadataEditHistory {
  id: string;
  policyId: string;
  fieldName: string;
  fieldType: 'STRUCTURED' | 'CUSTOM';
  oldValue?: string;
  newValue?: string;
  changeReason?: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE';
  changedBy: string;
  changedByRole: 'ADVISOR' | 'SUPPORT' | 'ADMIN';
  onBehalfOfAdvisorId?: string;
  onBehalfOfAdvisorName?: string;
  associatedDocumentVersion?: number;
  changedAt: string;
}

export interface PolicyMetadataExistsResponse {
  hasMetadata: boolean;
  hasStructuredDetails: boolean;
  hasCustomMetadata: boolean;
  customMetadataCount: number;
  editHistoryCount: number;
  documentStatus?: string;
  hasProviderSubmission: boolean;
  latestSubmissionDate?: string;
}

// Request DTOs
export interface SaveStructuredDetailsRequest {
  changeReason: string;
  associatedDocumentVersion?: number;
  fields: Record<string, unknown>;
}

export interface AddCustomMetadataRequest {
  key: string;
  value?: string;
  changeReason: string;
  associatedDocumentVersion?: number;
}

export interface UpdateCustomMetadataRequest {
  value?: string;
  changeReason: string;
}

// Field metadata for form rendering
export interface FieldMetadata {
  label: string;
  type: 'text' | 'textarea' | 'email' | 'number' | 'currency' | 'percent' | 'date' | 'select' | 'boolean' | 'phone' | 'country' | 'postalCode' | 'file';
  options?: { value: string; label: string }[];
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  /** When set, this field is only visible when the specified boolean field is true or select field equals 'YES' */
  visibleWhen?: string;
}

// Accordion section configuration matching Figma design
export interface AccordionSection {
  key: string;
  label: string;
  fields: string[];
}

/**
 * Accordion sections matching the Figma design.
 * Each section maps to a collapsible card in the Metadata tab.
 */
export const METADATA_ACCORDION_SECTIONS: AccordionSection[] = [
  // === Provider info (pension vs investment) ===
  { key: 'providerInfo', label: 'Provider information', fields: [
    'providerContactName', 'completedByName', 'completedByEmail', 'completionDate',
  ]},
  { key: 'investmentProviderInfo', label: 'Provider details', fields: [
    'completedByName', 'providerContactName', 'completedByEmail',
  ]},

  // === Policy holder details (shared) ===
  { key: 'policyholderDetails', label: 'Policy holder details', fields: [
    'customerName', 'customerDateOfBirth',
    'customerEmail', 'customerPhone',
    'customerCountry', 'customerAddress',
    'customerAddressLine2', 'customerAddressLine3',
    'customerCity', 'customerPostalCode',
  ]},

  // === Policy details (pension vs investment) ===
  { key: 'policyDetails', label: 'Policy details', fields: [
    'policyAccountNumber', 'typeOfPolicy', 'pensionType',
    'policyStatus',
    'closedReason',
    'closedProviderName', 'closedDateOfTransfer',
    'closedPolicyReference', 'closedPolicyType',
    'closedTransferType', 'closedTransferValue',
    'closedTotalEmployerContributions', 'closedTotalEmployeeContributions',
    'retiredBceDate', 'retiredBceValue',
    'retiredTaxFreeLumpSum', 'retiredPreviousTaxFreeLumpSums',
    'policyValue', 'currentValueDate',
    'transferValue', 'transferValueDate',
    'schemeRevenueRefNumber',
  ]},
  { key: 'investmentPolicyDetails', label: 'Policy details', fields: [
    'fullPolicyNumber', 'typeOfOwnership',
    'policyType', 'typeOfAccount',
    'riskLevel', 'policyStartDate',
    'amountInvested', 'currentPolicyValue', 'dateCurrentPolicyValue',
    'exitPenaltyDetails', 'exitTaxesPayable',
    'transferSurrenderValue', 'dateTransferSurrenderValue',
    'maturityDate',
    'payingStatus', 'premiumFrequency',
    'premiumAmount', 'indexationDetails',
    'namesOfLifeAssured',
  ]},

  // === Pension-only sections ===
  { key: 'employmentDetails', label: 'Employment details', fields: [
    'employerName', 'dateJoinedScheme',
    'firstDateOfEmployment', 'schemeNormalRetirementAge',
    'dateOfLeavingService', 'salaryAtDateOfLeaving',
    'severancePackageReceived', 'severanceWaivedLumpSum',
    'severanceDocumentUpload', 'severanceNotes',
  ]},
  { key: 'contributionDetails', label: 'Contribution details', fields: [
    'employerContribution', 'employeeContribution',
    'payingStatus', 'dateOfLastContribution',
    'regularPremiumAmount', 'regularPremiumFrequency',
    'singlePremiumAmount', 'singlePremiumFrequency',
    'indexationDetails', 'policyPaidBy',
    'dueDateNextPayment', 'dueAmountNextPayment',
    'transfersInFromAnotherScheme',
  ]},
  { key: 'crystallisedRetired', label: 'Crystallised / retired', fields: [
    'hasBeenCrystallised',
    'crystallisedDate', 'crystallisedAmount',
    'crystallisedProviderName', 'crystallisedQfmName',
    'taxFreeLumpSum', 'taxFreeLumpSumDate',
    'otherLumpSum', 'taxPaidOtherLumpSum', 'otherLumpSumDate',
    'additionalLumpSums',
    'crystallisedDocumentUpload',
    'isDrawingIncome', 'annualDrawdownPercent', 'lastAnnualDrawdown',
    'drawdownFrequency', 'drawdownPaymentDate',
  ]},

  // === Uploads (investment-only) ===
  { key: 'investmentUploads', label: 'Uploads', fields: [
    'valuationUpload', 'statementOfAccountUpload',
    'termsOfInvestmentUpload', 'policyDocumentUpload',
    'mifidCostsChargesUpload', 'mostRecentTaxPackUpload',
  ]},

  // === Investments (pension vs investment) ===
  { key: 'investments', label: 'Investments', fields: [
    'investmentFunds',
  ]},
  { key: 'investmentFunds', label: 'Investment funds', fields: [
    'investmentFunds',
  ]},

  // === Charges & fees (pension vs investment) ===
  { key: 'chargesFees', label: 'Charges & fees', fields: [
    'brokerAdvisoryAmc', 'commissionsOnPolicy', 'allocationPercentContributions',
    'exitChargesPenalties', 'transferOutCosts',
    'guaranteedReturns', 'terminalBonusDetails', 'otherChargeDetails',
    'chargesFeesDocUpload',
  ]},
  { key: 'investmentChargesFees', label: 'Charges & fees', fields: [
    'brokerAdvisoryAmc', 'commissionsOnPolicy', 'allocationPercentContributions',
  ]},

  // === Other charges (investment-only) ===
  { key: 'investmentOtherCharges', label: 'Other charges and details', fields: [
    'guaranteedReturnsDetails', 'terminalBonusDetails', 'otherChargeDetails',
  ]},

  // === Other information (pension vs investment) ===
  { key: 'additionalInfo', label: 'Other information', fields: [
    'hasPolicyBeenAssigned', 'policyAssignedTo', 'policyAssignedDate', 'policyAssignedDetails', 'assignedDocUpload',
    'hasPensionAdjustmentOrder', 'pensionAdjustmentDate', 'pensionAdjustmentDetails', 'pensionAdjustmentOrderDocument',
    'hasUniqueDeathBenefit', 'uniqueDeathBenefitExpiry', 'uniqueDeathBenefitDetails',
  ]},
  { key: 'investmentOtherInfo', label: 'Other information', fields: [
    'policyAssigned', 'policyAssignedTo', 'policyAssignedDate',
    'hasBareTrustOrDeed', 'bareTrustOrDeedTo',
    'uniqueDeathBenefit',
  ]},

  // === Pension-only: transfers, transaction history, other benefits ===
  { key: 'transfersIn', label: 'Transfer In', fields: [
    'hasTransferFromOtherSchemes',
    'detailAmountTransferred', 'detailProvider', 'detailDateTransferred', 'detailPolicyType',
    'detailReferenceNumber', 'detailSchemeName',
    'sameEmployment',
    'sameEmpDateTransferred', 'sameEmpAmountTransferred',
    'employeeContributionsTransferred', 'employerContributionsTransferred',
    'transferPreviousProviderName', 'transferPreviousPolicyNumber', 'transferType',
    'previousProvidersName', 'previousPolicyNumberNo',
    'employerDetails', 'transferDateJoinedService', 'transferDateJoinedScheme', 'transferDateOfLeavingService',
    'salaryAtLeavingService', 'totalEmployerContributions', 'totalEmployeeContributions',
    'transferFromOverseas', 'overseasCountry', 'wasTaxPaid', 'overseasTaxDetails',
    'transferTaxBackupUpload',
  ]},
  { key: 'transactionHistory', label: 'Transaction History', fields: [
    'transactionHistoryUpload', 'currentValuationUpload', 'sorpUpload',
  ]},
  { key: 'otherBenefits', label: 'Other associated benefits', fields: [
    'hasProtectionBenefits',
    'lifeCoverEnabled', 'lifeCoverCards',
    'incomeProtectionEnabled', 'incomeProtectionCards',
    'specifiedIllnessEnabled', 'specifiedIllnessCards',
  ]},
];

/**
 * Field metadata for form rendering.
 * Defines label, input type, placeholder, and help text for each field.
 */
export const FIELD_METADATA: Record<string, FieldMetadata> = {
  // Provider Information
  providerName: { label: 'Name of provider', type: 'text', placeholder: 'e.g., AIB Private Banking', required: true },
  providerContactName: { label: 'Provider contact name', type: 'text', required: true },
  providerEmail: { label: 'Email contact', type: 'email', placeholder: 'email@provider.ie', required: true },
  completedByName: { label: 'Completed by (name)', type: 'text', required: true },
  completedByEmail: { label: 'Completed by (email)', type: 'email', required: true },
  completionDate: { label: 'Completion date', type: 'date', required: true },

  // Policy holder details (matching Provider Landing Page: StepPolicyholderDetails)
  customerName: { label: 'Full name of policy holder', type: 'text', required: true },
  customerDateOfBirth: { label: 'Date of birth', type: 'date', required: true },
  customerEmail: { label: 'Email of policy holder', type: 'email', required: true },
  customerPhone: { label: 'Phone number', type: 'phone', placeholder: 'Phone of policy holder', required: true },
  customerCountry: { label: 'Country of residential address', type: 'country', required: true },
  customerAddress: { label: 'Address line 1', type: 'text', required: true },
  customerAddressLine2: { label: 'Address line 2', type: 'text', required: true },
  customerAddressLine3: { label: 'Address line 3', type: 'text' },
  customerCity: { label: 'City / Town', type: 'text', required: true },
  customerPostalCode: { label: 'Eircode', type: 'postalCode' },

  // Policy Details (matching Provider Landing Page: StepPolicyDetails + StepPolicyStatus)
  typeOfPolicy: {
    label: 'Type of policy',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Select type...' },
      { value: 'PENSION', label: 'Pension' },
      { value: 'INVESTMENT', label: 'Investment' },
      { value: 'INSURANCE', label: 'Insurance' },
      { value: 'BANKING', label: 'Banking' }
    ]
  },
  pensionType: {
    label: 'Pension Type',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Select pension type...' },
      { value: 'PRB_BOB', label: 'PRB / Buy-out Bond' },
      { value: 'PRSA_STANDARD', label: 'PRSA (Standard)' },
      { value: 'PRSA_NON_STANDARD', label: 'PRSA (Non-Standard)' },
      { value: 'PRSA_AVC', label: 'PRSA AVC' },
      { value: 'PERSONAL_PENSION', label: 'Personal Pension' },
      { value: 'DEFINED_CONTRIBUTION', label: 'Defined Contribution' },
      { value: 'EXECUTIVE_PENSION', label: 'Executive Pension' },
      { value: 'MASTERTRUST', label: 'Mastertrust' },
      { value: 'DIRECTORS_PENSION', label: "Director's Pension" },
      { value: 'ARF', label: 'ARF (Approved Retirement Fund)' },
      { value: 'VESTED_PRSA', label: 'Vested PRSA' }
    ]
  },
  policyAccountNumber: { label: 'Policy number / Plan Reference No / Member Reference No', type: 'text', required: true },
  policyValue: { label: 'Current value', type: 'currency', required: true },
  currentValueDate: { label: 'Date as at current value', type: 'date', required: true },
  transferValue: { label: 'Transfer value', type: 'currency', required: true },
  transferValueDate: { label: 'Date as at transfer value', type: 'date', required: true },
  schemeRevenueRefNumber: { label: 'Scheme Revenue Reference Number', type: 'text', placeholder: 'e.g., SF123456 or PB123456', helpText: 'eg. SF123456 or sometimes PB123456' },
  policyStatus: {
    label: 'What is the policy status?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Select status...' },
      { value: 'ACTIVE', label: 'Active' },
      { value: 'CLOSED', label: 'Closed' }
    ]
  },
  closedReason: {
    label: 'Closed status',
    type: 'select',
    required: true,
    visibleWhen: 'policyStatus=CLOSED',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'RETIRED', label: 'Retired' },
      { value: 'TRANSFERRED_OUT', label: 'Transferred out' }
    ]
  },
  closedProviderName: { label: 'Provider name', type: 'text', required: true, visibleWhen: 'closedReason' },
  closedDateOfTransfer: { label: 'Date of Transfer', type: 'date', required: true, visibleWhen: 'closedReason' },
  closedPolicyReference: { label: 'Policy reference number', type: 'text', required: true, visibleWhen: 'closedReason' },
  closedPolicyType: {
    label: 'Type of Policy',
    type: 'select',
    required: true,
    visibleWhen: 'closedReason',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'PRB_BOB', label: 'PRB / Buy-out Bond' },
      { value: 'PRSA_STANDARD', label: 'PRSA (Standard)' },
      { value: 'PRSA_NON_STANDARD', label: 'PRSA (Non-Standard)' },
      { value: 'PRSA_AVC', label: 'PRSA AVC' },
      { value: 'PERSONAL_PENSION', label: 'Personal Pension' },
      { value: 'DEFINED_CONTRIBUTION', label: 'Defined Contribution' },
      { value: 'EXECUTIVE_PENSION', label: 'Executive Pension' },
      { value: 'MASTERTRUST', label: 'Mastertrust' },
      { value: 'DIRECTORS_PENSION', label: "Director's Pension" },
      { value: 'ARF', label: 'ARF (Approved Retirement Fund)' },
      { value: 'VESTED_PRSA', label: 'Vested PRSA' }
    ]
  },
  closedTransferType: {
    label: 'Type of Transfer',
    type: 'select',
    required: true,
    visibleWhen: 'closedReason',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'CASH', label: 'Cash' },
      { value: 'IN_SPECIE', label: 'In-specie' }
    ]
  },
  closedTransferValue: { label: 'Value as at transfer date', type: 'currency', required: true, visibleWhen: 'closedReason' },
  closedTotalEmployerContributions: { label: 'Total Employer contributions', type: 'text', required: true, visibleWhen: 'closedReason=TRANSFERRED_OUT' },
  closedTotalEmployeeContributions: { label: 'Total Employee Contributions', type: 'text', required: true, visibleWhen: 'closedReason=TRANSFERRED_OUT' },
  retiredBceDate: { label: 'Date of Benefit Crystallisation Event', type: 'date', required: true, visibleWhen: 'closedReason=RETIRED' },
  retiredBceValue: { label: 'Value of Policy as a BCE', type: 'currency', required: true, visibleWhen: 'closedReason=RETIRED' },
  retiredTaxFreeLumpSum: { label: 'Tax Free Lump Sum', type: 'currency', required: true, visibleWhen: 'closedReason=RETIRED' },
  retiredPreviousTaxFreeLumpSums: { label: 'Details of other lump sums on record (€, dates, tax paid and connected policy)', type: 'textarea', visibleWhen: 'closedReason=RETIRED' },

  // Employment details (matching Provider Landing Page: StepEmploymentDetails)
  employerName: { label: 'Employer name', type: 'text', required: true },
  dateJoinedScheme: { label: 'Date joined scheme', type: 'date', required: true },
  firstDateOfEmployment: { label: 'First date of employment', type: 'date', required: true },
  schemeNormalRetirementAge: { label: 'Scheme/policy NRA', type: 'text', required: true },
  dateOfLeavingService: { label: 'Date of leaving service', type: 'date', required: true },
  salaryAtDateOfLeaving: { label: 'Salary as at date of leaving service', type: 'currency', required: true },
  severancePackageReceived: {
    label: 'Did employee receive severance package relating to this employment?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  severanceWaivedLumpSum: {
    label: 'Did the policyholder waive their right to a tax free lump sum from the occupational pension scheme?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  severanceDocumentUpload: { label: 'Upload documents', type: 'file', visibleWhen: 'severancePackageReceived=Yes', helpText: 'Upload severance documentation' },
  severanceNotes: { label: 'Severance package notes', type: 'textarea', placeholder: 'Severance package notes', visibleWhen: 'severancePackageReceived=Yes' },

  // Contribution Details (matching Provider Landing Page: StepContributionDetails)
  employerContribution: { label: 'Employer Contribution to date', type: 'currency', required: true },
  employeeContribution: { label: 'Employee Contribution to date', type: 'currency', required: true },
  payingStatus: {
    label: 'Paying status',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Paid Up', label: 'Paid Up' },
      { value: 'In Force', label: 'In Force' },
      { value: 'Deferred', label: 'Deferred' }
    ]
  },
  dateOfLastContribution: { label: 'Date of last contribution', type: 'date', required: true },
  regularPremiumAmount: { label: 'Regular Premium', type: 'currency', required: true },
  regularPremiumFrequency: {
    label: 'Regular Premium frequency',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Quarterly', label: 'Quarterly' },
      { value: 'Semi-Annual', label: 'Semi-Annual' },
      { value: 'Annual', label: 'Annual' },
      { value: 'Single', label: 'Single' },
      { value: 'N/A', label: 'N/A' }
    ]
  },
  singlePremiumAmount: { label: 'Single Premium', type: 'currency', required: true },
  singlePremiumFrequency: {
    label: 'Single Premium frequency',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Quarterly', label: 'Quarterly' },
      { value: 'Semi-Annual', label: 'Semi-Annual' },
      { value: 'Annual', label: 'Annual' },
      { value: 'Single', label: 'Single' },
      { value: 'N/A', label: 'N/A' }
    ]
  },
  indexationDetails: { label: 'Indexation details if applicable', type: 'text' },
  policyPaidBy: {
    label: 'Is the policy paid by',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Direct Debit', label: 'Direct Debit' },
      { value: 'Standing Order', label: 'Standing Order' },
      { value: 'Manual Adhoc Transfer', label: 'Manual Adhoc Transfer' }
    ]
  },
  dueDateNextPayment: { label: 'Due date of next payment', type: 'date', required: true },
  dueAmountNextPayment: { label: 'Due amount of next payment', type: 'currency', required: true },
  transfersInFromAnotherScheme: { label: 'Transfers in from another scheme', type: 'currency', required: true },

  // Crystallised / Retired (matching Provider Landing Page: StepCrystallisedRetired)
  hasBeenCrystallised: {
    label: 'Has the policy been crystallised/retired?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  crystallisedDate: { label: 'Date of Benefit Crystallisation Event', type: 'date', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  crystallisedAmount: { label: 'Value of Policy as at BCE', type: 'currency', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  crystallisedProviderName: {
    label: 'Provider name who Crystallised the policy',
    type: 'select',
    required: true,
    visibleWhen: 'hasBeenCrystallised=Yes',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Irish Life', label: 'Irish Life' },
      { value: 'Zurich', label: 'Zurich' },
      { value: 'Aviva', label: 'Aviva' },
      { value: 'New Ireland', label: 'New Ireland' },
      { value: 'Standard Life', label: 'Standard Life' },
      { value: 'Royal London', label: 'Royal London' },
      { value: 'Canada Life', label: 'Canada Life' },
      { value: 'Other', label: 'Other' }
    ]
  },
  crystallisedQfmName: { label: 'Name of Qualifying Fund Manager (QFM)', type: 'text', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  taxFreeLumpSum: { label: 'Tax Free Lump Sum', type: 'currency', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  taxFreeLumpSumDate: { label: 'Date of Tax Free', type: 'date', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  otherLumpSum: { label: 'Other Lump Sum', type: 'currency', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  taxPaidOtherLumpSum: { label: 'Tax Paid On Other Lump Sum', type: 'currency', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  otherLumpSumDate: { label: 'Date of Lump Sum', type: 'date', required: true, visibleWhen: 'hasBeenCrystallised=Yes' },
  additionalLumpSums: { label: 'Additional Lump Sums', type: 'text', visibleWhen: 'hasBeenCrystallised=Yes' },
  crystallisedDocumentUpload: { label: 'Upload Documents', type: 'file', visibleWhen: 'hasBeenCrystallised=Yes' },
  isDrawingIncome: {
    label: 'Is the Policyholder drawing income from the policy?',
    type: 'select',
    required: true,
    visibleWhen: 'hasBeenCrystallised=Yes',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  annualDrawdownPercent: { label: 'Annual Drawdown %', type: 'percent', required: true, visibleWhen: 'isDrawingIncome=Yes' },
  lastAnnualDrawdown: { label: 'Last Annual Drawdown', type: 'currency', required: true, visibleWhen: 'isDrawingIncome=Yes' },
  drawdownFrequency: {
    label: 'Drawdown Payment Frequency',
    type: 'select',
    required: true,
    visibleWhen: 'isDrawingIncome=Yes',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Quarterly', label: 'Quarterly' },
      { value: 'Semi-Annual', label: 'Semi-Annual' },
      { value: 'Annual', label: 'Annual' },
      { value: 'Ad Hoc', label: 'Ad Hoc' }
    ]
  },
  drawdownPaymentDate: { label: 'Date of Drawdown Payment', type: 'date', required: true, visibleWhen: 'isDrawingIncome=Yes' },

  // Investments (matching Provider Landing Page: StepInvestments — array of fund entries)
  investmentFunds: { label: 'Investment Funds', type: 'text' },

  // Charges & Fees (matching Provider Landing Page: StepChargesFees)
  brokerAdvisoryAmc: { label: 'Fund Management Charge/ Broker Trail', type: 'percent', required: true },
  commissionsOnPolicy: { label: 'Commissions on policy or account', type: 'percent', required: true },
  allocationPercentContributions: { label: 'Allocation % of Contributions', type: 'percent', required: true },
  exitChargesPenalties: { label: 'Exit Charges/ penalties Details', type: 'text', required: true },
  transferOutCosts: { label: 'Transfer out costs', type: 'text', required: true },

  // Other Charges & Details (matching Provider Landing Page: StepChargesFees)
  guaranteedReturns: { label: 'Guaranteed Returns Details', type: 'text', required: true },
  terminalBonusDetails: { label: 'Terminal Bonus Details', type: 'text', required: true },
  otherChargeDetails: { label: 'Other Charge Details', type: 'textarea', required: true, placeholder: 'Other Charge Details' },
  chargesFeesDocUpload: { label: 'Upload Documents', type: 'file' },

  // Additional Information (matching Provider Landing Page: StepOtherInformation)
  hasPolicyBeenAssigned: {
    label: 'Has policy been assigned?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  policyAssignedTo: { label: 'To whom has policy been assigned?', type: 'text', required: true, visibleWhen: 'hasPolicyBeenAssigned=Yes' },
  policyAssignedDate: { label: 'Date the policy was assigned', type: 'date', required: true, visibleWhen: 'hasPolicyBeenAssigned=Yes' },
  policyAssignedDetails: { label: 'Other Details', type: 'textarea', placeholder: 'Other Details', visibleWhen: 'hasPolicyBeenAssigned=Yes' },
  assignedDocUpload: { label: 'Upload Documents', type: 'file', visibleWhen: 'hasPolicyBeenAssigned=Yes' },
  hasPensionAdjustmentOrder: {
    label: 'Has a Pension Adjustment Order been issued or is in the process of being issued?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  pensionAdjustmentDate: { label: 'Date of the Pension Adjustment Order', type: 'date', required: true, visibleWhen: 'hasPensionAdjustmentOrder=Yes' },
  pensionAdjustmentDetails: { label: 'Other Details', type: 'textarea', placeholder: 'Other Details', visibleWhen: 'hasPensionAdjustmentOrder=Yes' },
  pensionAdjustmentOrderDocument: { label: 'Upload PAO details', type: 'file', visibleWhen: 'hasPensionAdjustmentOrder=Yes' },
  hasUniqueDeathBenefit: {
    label: 'Is there a unique death benefit applying to the policy?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  uniqueDeathBenefitExpiry: { label: 'Expiry date of the unique death benefit', type: 'date', required: true, visibleWhen: 'hasUniqueDeathBenefit=Yes' },
  uniqueDeathBenefitDetails: { label: 'Please provide details', type: 'textarea', placeholder: 'Please provide details', visibleWhen: 'hasUniqueDeathBenefit=Yes' },

  // Transfers IN (matching Provider Landing Page: StepTransfersIn)
  hasTransferFromOtherSchemes: {
    label: 'Has the policy received any transfers from other schemes or arrangements?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  detailAmountTransferred: { label: 'Amount Transferred in', type: 'currency', required: true, visibleWhen: 'hasTransferFromOtherSchemes=Yes' },
  detailProvider: {
    label: 'Policy/ Account Provider',
    type: 'select',
    required: true,
    visibleWhen: 'hasTransferFromOtherSchemes=Yes',
    options: [
      { value: '', label: 'Choose a provider...' },
      { value: 'Irish Life', label: 'Irish Life' },
      { value: 'Zurich', label: 'Zurich' },
      { value: 'Aviva', label: 'Aviva' },
      { value: 'New Ireland', label: 'New Ireland' },
      { value: 'Standard Life', label: 'Standard Life' },
      { value: 'Royal London', label: 'Royal London' },
      { value: 'Canada Life', label: 'Canada Life' },
      { value: 'Other', label: 'Other' }
    ]
  },
  detailDateTransferred: { label: 'Date Transferred in', type: 'date', required: true, visibleWhen: 'hasTransferFromOtherSchemes=Yes' },
  detailPolicyType: {
    label: 'Policy/Account/Pension Type',
    type: 'select',
    required: true,
    visibleWhen: 'hasTransferFromOtherSchemes=Yes',
    options: [
      { value: '', label: 'Choose a type...' },
      { value: 'PRB_BOB', label: 'PRB / Buy-out Bond' },
      { value: 'PRSA_STANDARD', label: 'PRSA (Standard)' },
      { value: 'PRSA_NON_STANDARD', label: 'PRSA (Non-Standard)' },
      { value: 'PRSA_AVC', label: 'PRSA AVC' },
      { value: 'PERSONAL_PENSION', label: 'Personal Pension' },
      { value: 'DEFINED_CONTRIBUTION', label: 'Defined Contribution' },
      { value: 'EXECUTIVE_PENSION', label: 'Executive Pension' },
      { value: 'MASTERTRUST', label: 'Mastertrust' },
      { value: 'DIRECTORS_PENSION', label: "Director's Pension" },
      { value: 'ARF', label: 'ARF (Approved Retirement Fund)' },
      { value: 'VESTED_PRSA', label: 'Vested PRSA' }
    ]
  },
  detailReferenceNumber: { label: 'Policy/Account Reference Number', type: 'text', required: true, visibleWhen: 'hasTransferFromOtherSchemes=Yes' },
  detailSchemeName: { label: 'Name of Scheme transfer received from', type: 'text', required: true, visibleWhen: 'hasTransferFromOtherSchemes=Yes' },
  sameEmployment: {
    label: 'Was the transfer in relating to the same employment',
    type: 'select',
    required: true,
    visibleWhen: 'hasTransferFromOtherSchemes=Yes',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  // sameEmployment = Yes fields
  sameEmpDateTransferred: { label: 'Date transferred in', type: 'date', required: true, visibleWhen: 'sameEmployment=Yes' },
  sameEmpAmountTransferred: { label: 'Amount transferred in', type: 'currency', required: true, visibleWhen: 'sameEmployment=Yes' },
  employeeContributionsTransferred: { label: 'Employee Contributions Transferred Over', type: 'text', required: true, visibleWhen: 'sameEmployment=Yes' },
  employerContributionsTransferred: { label: 'Employer contributions Transferred Over', type: 'text', required: true, visibleWhen: 'sameEmployment=Yes' },
  transferPreviousProviderName: { label: 'Previous providers name', type: 'text', required: true, visibleWhen: 'sameEmployment=Yes' },
  transferPreviousPolicyNumber: { label: 'Previous policy number', type: 'text', required: true, visibleWhen: 'sameEmployment=Yes' },
  transferType: {
    label: 'Type of Transfer',
    type: 'select',
    required: true,
    visibleWhen: 'sameEmployment=Yes',
    options: [
      { value: '', label: 'Select a option...' },
      { value: 'Cash', label: 'Cash' },
      { value: 'In-specie', label: 'In-specie' }
    ]
  },
  // sameEmployment = No fields
  previousProvidersName: { label: 'Previous providers name', type: 'text', required: true, visibleWhen: 'sameEmployment=No' },
  previousPolicyNumberNo: { label: 'Previous policy number', type: 'text', required: true, visibleWhen: 'sameEmployment=No' },
  employerDetails: { label: 'Employer details', type: 'text', required: true, visibleWhen: 'sameEmployment=No' },
  transferDateJoinedService: { label: 'Date Joined service', type: 'date', required: true, visibleWhen: 'sameEmployment=No' },
  transferDateJoinedScheme: { label: 'Date Joined Scheme', type: 'date', required: true, visibleWhen: 'sameEmployment=No' },
  transferDateOfLeavingService: { label: 'Date of Leaving Service', type: 'date', required: true, visibleWhen: 'sameEmployment=No' },
  salaryAtLeavingService: { label: 'Salary as at leaving service', type: 'currency', required: true, visibleWhen: 'sameEmployment=No' },
  totalEmployerContributions: { label: 'Total Employer contributions', type: 'text', required: true, visibleWhen: 'sameEmployment=No' },
  totalEmployeeContributions: { label: 'Total Employee Contributions', type: 'text', required: true, visibleWhen: 'sameEmployment=No' },
  transferFromOverseas: {
    label: 'Did the transfer come from overseas?',
    type: 'select',
    required: true,
    visibleWhen: 'sameEmployment=No',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  overseasCountry: { label: 'What country did the overseas pension come from?', type: 'select', required: true, visibleWhen: 'transferFromOverseas=Yes',
    options: [
      { value: '', label: 'Select a country...' },
      { value: 'IE', label: 'Ireland' }, { value: 'GB', label: 'United Kingdom' }, { value: 'US', label: 'United States' },
      { value: 'AU', label: 'Australia' }, { value: 'CA', label: 'Canada' }, { value: 'NZ', label: 'New Zealand' },
      { value: 'DE', label: 'Germany' }, { value: 'FR', label: 'France' }, { value: 'ES', label: 'Spain' },
      { value: 'IT', label: 'Italy' }, { value: 'NL', label: 'Netherlands' }, { value: 'BE', label: 'Belgium' },
      { value: 'PT', label: 'Portugal' }, { value: 'AT', label: 'Austria' }, { value: 'CH', label: 'Switzerland' },
      { value: 'SE', label: 'Sweden' }, { value: 'DK', label: 'Denmark' }, { value: 'NO', label: 'Norway' },
      { value: 'FI', label: 'Finland' }, { value: 'PL', label: 'Poland' }, { value: 'CZ', label: 'Czech Republic' },
      { value: 'HU', label: 'Hungary' }, { value: 'RO', label: 'Romania' }, { value: 'BG', label: 'Bulgaria' },
      { value: 'HR', label: 'Croatia' }, { value: 'SK', label: 'Slovakia' }, { value: 'SI', label: 'Slovenia' },
      { value: 'LT', label: 'Lithuania' }, { value: 'LV', label: 'Latvia' }, { value: 'EE', label: 'Estonia' },
      { value: 'MT', label: 'Malta' }, { value: 'CY', label: 'Cyprus' }, { value: 'LU', label: 'Luxembourg' },
      { value: 'IS', label: 'Iceland' }, { value: 'ZA', label: 'South Africa' }, { value: 'AE', label: 'United Arab Emirates' },
      { value: 'SG', label: 'Singapore' }, { value: 'HK', label: 'Hong Kong' }, { value: 'JP', label: 'Japan' },
      { value: 'IN', label: 'India' }, { value: 'CN', label: 'China' }, { value: 'BR', label: 'Brazil' },
      { value: 'MX', label: 'Mexico' }, { value: 'AR', label: 'Argentina' }, { value: 'CL', label: 'Chile' },
      { value: 'CO', label: 'Colombia' }, { value: 'SA', label: 'Saudi Arabia' }, { value: 'IL', label: 'Israel' },
      { value: 'TR', label: 'Turkey' }, { value: 'RU', label: 'Russia' }, { value: 'KR', label: 'South Korea' },
      { value: 'TW', label: 'Taiwan' }, { value: 'TH', label: 'Thailand' }, { value: 'MY', label: 'Malaysia' },
      { value: 'PH', label: 'Philippines' }, { value: 'ID', label: 'Indonesia' }, { value: 'PK', label: 'Pakistan' },
      { value: 'EG', label: 'Egypt' }, { value: 'NG', label: 'Nigeria' }, { value: 'GR', label: 'Greece' },
      { value: 'DZ', label: 'Algeria' }
    ]
  },
  wasTaxPaid: {
    label: 'Was there tax paid on transfer out?',
    type: 'select',
    required: true,
    visibleWhen: 'transferFromOverseas=Yes',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  overseasTaxDetails: { label: 'Please provide details and backup.', type: 'textarea', required: true, visibleWhen: 'wasTaxPaid=Yes', placeholder: 'Overseas tax paid details' },
  transferTaxBackupUpload: { label: 'Upload Documents', type: 'file', visibleWhen: 'hasTransferFromOtherSchemes=Yes' },

  // Transaction History (matching Provider Landing Page: StepTransactionHistory)
  transactionHistoryUpload: { label: 'Please attach full transaction history of the account since inception', type: 'file' },
  currentValuationUpload: { label: 'Please attached a current valuation of the account', type: 'file' },
  sorpUpload: { label: 'Please attached a Statement of Reasonable Projection (SORP)', type: 'file' },

  // Other Associated Benefits (matching Provider Landing Page: StepAssociatedBenefits)
  hasProtectionBenefits: {
    label: 'Does the policyholder/beneficiary have any attaching protection benefits?',
    type: 'select',
    required: true,
    helpText: '(eg. Death in service, Executive Income Protection, Income Protection, Wage Protection, Specified Illness)',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ]
  },
  lifeCoverEnabled: { label: 'Life Cover', type: 'boolean', visibleWhen: 'hasProtectionBenefits=Yes' },
  lifeCoverCards: { label: 'Life Cover Cards', type: 'text' },
  incomeProtectionEnabled: { label: 'Income Protection', type: 'boolean', visibleWhen: 'hasProtectionBenefits=Yes' },
  incomeProtectionCards: { label: 'Income Protection Cards', type: 'text' },
  specifiedIllnessEnabled: { label: 'Specified Illness', type: 'boolean', visibleWhen: 'hasProtectionBenefits=Yes' },
  specifiedIllnessCards: { label: 'Specified Illness Cards', type: 'text' },

  // ========================================================================
  // Investment-specific fields
  // ========================================================================
  investmentType: {
    label: 'Investment Type',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Select investment type...' },
      { value: 'INVESTMENT_BOND', label: 'Investment Bond' },
      { value: 'REGULAR_SAVER', label: 'Regular Saver' },
      { value: 'CHILDS_SAVER', label: "Child's Saver" },
      { value: 'TRADING_ACCOUNTS', label: 'Trading Accounts' },
      { value: 'INVESTMENT_PORTFOLIO', label: 'Investment Portfolio' },
    ]
  },
  typeOfOwnership: {
    label: 'Type of ownership',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'BARE_TRUST', label: 'Bare Trust' },
      { value: 'TRADING_ACCOUNTS', label: 'Trading Accounts' },
      { value: 'INVESTMENT_PORTFOLIO', label: 'Investment Portfolio' },
      { value: 'STRUCTURED_PRODUCTS', label: 'Structured Products' },
    ]
  },
  accountType: {
    label: 'Account type',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'EXECUTION_ONLY', label: 'Execution Only' },
      { value: 'ADVISORY', label: 'Advisory' },
      { value: 'DISCRETIONARY', label: 'Discretionary' },
    ]
  },
  namesOfLifeAssured: { label: 'Names of life assured', type: 'text' },
  currentPolicyValue: { label: 'Current / Policy value', type: 'currency', required: true },
  dateCurrentPolicyValue: { label: 'Date as at current/policy value', type: 'date', required: true },
  exitPenaltyDetails: { label: 'Exit penalty details', type: 'text', required: true },
  transferSurrenderValue: { label: 'Transfer/Surrender value', type: 'currency', required: true },
  dateTransferSurrenderValue: { label: 'Date as at transfer/surrender value', type: 'date', required: true },
  investmentPayingStatus: {
    label: 'Paying status',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'IN_FORCE', label: 'In Force' },
      { value: 'PAID_UP', label: 'Paid Up' },
    ]
  },
  investmentPremiumFrequency: {
    label: 'Premium frequency',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'MONTHLY', label: 'Monthly' },
      { value: 'QUARTERLY', label: 'Quarterly' },
      { value: 'ANNUALLY', label: 'Annually' },
      { value: 'ADHOC', label: 'Ad Hoc' },
    ]
  },
  riskLevel: { label: 'Risk Level', type: 'text' },
  policyStartDate: { label: 'Policy start date', type: 'date' },
  amountInvested: { label: 'Amount Invested (bookcost)', type: 'currency' },
  exitTaxesPayable: { label: 'Exit Taxes Payable on surrender', type: 'text' },
  maturityDate: { label: 'Maturity Date', type: 'date' },
  premiumAmount: { label: 'Premium Amount', type: 'currency' },
  // Investment policy details (keys matching provider form field names)
  fullPolicyNumber: { label: 'Policy number / Plan Reference No / Member Reference No', type: 'text', required: true },
  policyType: {
    label: 'Investment Type',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Select investment type...' },
      { value: 'INVESTMENT_BOND', label: 'Investment Bond' },
      { value: 'REGULAR_SAVER', label: 'Regular Saver' },
      { value: 'CHILDS_SAVER', label: "Child's Saver" },
      { value: 'TRADING_ACCOUNTS', label: 'Trading Accounts' },
      { value: 'INVESTMENT_PORTFOLIO', label: 'Investment Portfolio' },
    ]
  },
  typeOfAccount: {
    label: 'Type of Account',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'EXECUTION_ONLY', label: 'Execution Only' },
      { value: 'ADVISORY', label: 'Advisory' },
      { value: 'DISCRETIONARY', label: 'Discretionary' },
    ]
  },
  premiumFrequency: {
    label: 'Premium Frequency',
    type: 'select',
    options: [
      { value: '', label: 'Select...' },
      { value: 'MONTHLY', label: 'Monthly' },
      { value: 'QUARTERLY', label: 'Quarterly' },
      { value: 'ANNUALLY', label: 'Annually' },
      { value: 'ADHOC', label: 'Ad Hoc' },
    ]
  },

  // Investment other information (keys matching provider form field names)
  policyAssigned: {
    label: 'Has policy/account been assigned or pledged?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ]
  },
  uniqueDeathBenefit: {
    label: 'Is there a unique death benefit applying to the policy?',
    type: 'select',
    required: true,
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ]
  },

  // Investment other charges (keys matching provider form field names)
  guaranteedReturnsDetails: { label: 'Guaranteed Returns Details', type: 'text', required: true },

  // Investment uploads
  valuationUpload: { label: 'Valuation (showing full breakdown and asset allocation)', type: 'file' },
  statementOfAccountUpload: { label: 'Statement of account since inception', type: 'file' },
  termsOfInvestmentUpload: { label: 'Terms of Investment Document', type: 'file' },
  policyDocumentUpload: { label: 'Policy Document', type: 'file' },
  mifidCostsChargesUpload: { label: 'Mifid Costs & Charges Report', type: 'file' },
  mostRecentTaxPackUpload: { label: 'Most recent Tax Pack', type: 'file' },

  hasBareTrustOrDeed: {
    label: 'Has policy a bare trust or deed of assignment?',
    type: 'select',
    options: [
      { value: '', label: 'Choose an option...' },
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ]
  },
  bareTrustOrDeedTo: { label: 'To whom?', type: 'text', visibleWhen: 'hasBareTrustOrDeed=Yes' },
  otherNotes: { label: 'Other information / notes', type: 'textarea' },
};
